import { DateTime } from 'luxon'

import type { DiscountDurationTextKey } from '@/open-web/components/UpgradeFlow/AddonPriceSection/utils/getDiscountDurationText'
import { getDiscountDurationText } from '@/open-web/components/UpgradeFlow/AddonPriceSection/utils/getDiscountDurationText'
import type { CostElement } from '@/open-web/services/calculators/calculateContractTemplateCost'
import {
  getAddonsMonthlyDiscounts,
  getContractMonthlyDiscounts,
  sumCostObjects,
  sumPricesByType,
} from '@/open-web/services/calculators/utils/utils'
import { DATE_FORMAT_FOR_API } from '@/open-web/utils/constants'
import type { DurationType } from '@/shared/graphql/schema/commonBackend/graphql'
import type {
  EnrichedAvailableAddon,
  EnrichedBundledAddonTemplate,
  EnrichedContractTemplate,
} from '@/shared/services/campaignDataResolver'
import {
  isEnergyDiscountElement,
  isEnergyElement,
  isMonthlyFeeDiscountElement,
  isMonthlyFeeElement,
} from '@/shared/utils/tariffElementUtils'

type CalculateTotalPriceProps = {
  contractTemplate: EnrichedContractTemplate
  selectedAddonsTariffNos?: number[]
}

export type CalculateTotalPriceResult = {
  /**
   * Total price with discounts applied.
   * - includes addons, bundled addons with discounts
   * - includes main product with discounts
   */
  totalPriceWithDiscount: CostElement
  /**
   * Total price without discounts.
   * - includes addons, bundled addons without discounts
   * - includes main product without discounts
   */
  total: CostElement
  /**
   * Indicates if any addon has a discount applied.
   * This is used to determine if we should show the detailed discount information in the UI.
   * For now, if there is addons with discount we shouldn't show the discount information in the UI.
   * - If true, at least one addon has a discount.
   * - If false, no addons have discounts.
   */
  hasSelectedAddonWithDiscount: boolean
  /**
   * Main product total price, without addons or bundled addons.
   */
  mainProduct: {
    total: CostElement
    totalPriceWithDiscount: CostElement
    /**
     * Discount duration information. Used to display discount duration in the UI.
     */
    discount?: {
      type: DiscountDurationTextKey
      duration: {
        amount?: number
        type: DurationType
      }
    }
  }
}

/**
 * Simple price calculation function for Norwegian contracts. It calculates the total price
 * based on the contract template, selected addons, bundled addons and their respective prices.
 * Price of the main product is calculated separately from the addons.
 *
 * TODO: Adjust this method to support Finland.
 */
export const calculateTotalPrice = ({
  contractTemplate,
  selectedAddonsTariffNos,
}: CalculateTotalPriceProps): CalculateTotalPriceResult | null => {
  try {
    const startDate = DateTime.now().toFormat(DATE_FORMAT_FOR_API)
    const selectedAddons = contractTemplate.availableAddons.filter((addon) =>
      selectedAddonsTariffNos?.includes(addon.tariffNo),
    )

    // Get all addons, including bundled ones
    const addons: (EnrichedAvailableAddon | EnrichedBundledAddonTemplate)[] = [
      ...contractTemplate.bundledAddons,
      ...selectedAddons,
    ]

    // Calculate main electricity product total and total with discounts
    const mainProductTotal = {
      energy: sumPricesByType(contractTemplate, [], startDate, isEnergyElement),
      monthlyFee: sumPricesByType(contractTemplate, [], startDate, isMonthlyFeeElement),
    }

    // Calculate total price for all addons and main product
    const total = {
      energy: sumPricesByType(contractTemplate, addons, startDate, isEnergyElement),
      monthlyFee: sumPricesByType(contractTemplate, addons, startDate, isMonthlyFeeElement),
    }

    const addonDiscounts = getAddonsMonthlyDiscounts(addons)
    const contractDiscounts = getContractMonthlyDiscounts(contractTemplate, 1)
    const discountsTotal = sumCostObjects(contractDiscounts, addonDiscounts)
    const totalPriceWithDiscount = sumCostObjects(total, discountsTotal)
    const mainProductTotalWithDiscount = sumCostObjects(mainProductTotal, contractDiscounts)

    const energyDiscountElement = contractTemplate.discountElements?.find((el) =>
      isEnergyDiscountElement(el.type),
    )
    const monthlyFeeDiscountElement = contractTemplate.discountElements?.find((el) =>
      isMonthlyFeeDiscountElement(el.type),
    )

    const hasSelectedAddonWithDiscount =
      addonDiscounts.energy.priceExclVat < 0 || addonDiscounts.monthlyFee.priceExclVat < 0

    const discountType = getDiscountDurationText({
      hasEnergyDiscount: Boolean(energyDiscountElement),
      hasMonthlyFeeDiscount: Boolean(monthlyFeeDiscountElement),
    })

    // When
    // - energyAndMonthlyFee || monthlyFee is selected, we use the monthly fee discount element
    // - energy is selected, we use the energy discount element
    const discountedElement =
      discountType === 'energy' ? energyDiscountElement : monthlyFeeDiscountElement

    const discount = discountType
      ? {
          type: discountType,
          duration: {
            amount: discountedElement?.duration?.amount,
            type: discountedElement?.duration?.type ?? 'MONTH',
          },
        }
      : undefined

    return {
      totalPriceWithDiscount,
      total,
      hasSelectedAddonWithDiscount,
      mainProduct: {
        total: mainProductTotal,
        totalPriceWithDiscount: mainProductTotalWithDiscount,
        discount,
      },
    }
  } catch {
    return null
  }
}
